import { skipToken } from '@reduxjs/toolkit/query';
import { useGetAssetMetricsQuery } from '@src/providers/CoinMetricsApiProvider';
import { useMemo } from 'react';
import { groupByAsset, type TimeSeriesPoint } from '../AggregatedMetric/aggregation';
import { CM_FETCH_LIMIT_PER_ASSET, CM_MAX_PAGE_SIZE } from '../core/tokens';

interface UseComparisonTimeseriesParams {
  cmAssetIds: string[];
  metric: string;
}

interface UseComparisonTimeseriesResult {
  dataByAsset: ReadonlyMap<string, TimeSeriesPoint[]>;
  isFetching: boolean;
  isError: boolean;
  isTruncated: boolean;
}

const EMPTY_MAP: ReadonlyMap<string, TimeSeriesPoint[]> = new Map();

export function useComparisonTimeseries({
  cmAssetIds,
  metric,
}: UseComparisonTimeseriesParams): UseComparisonTimeseriesResult {
  const assetsParam = useMemo(() => [...cmAssetIds].toSorted().join(','), [cmAssetIds]);

  const queryArg = useMemo(
    () =>
      cmAssetIds.length === 0 || !metric
        ? skipToken
        : {
            assets: assetsParam,
            metrics: metric,
            frequency: '1d' as const,
            limit_per_asset: CM_FETCH_LIMIT_PER_ASSET,
            page_size: CM_MAX_PAGE_SIZE,
            paging_from: 'end' as const,
          },
    [assetsParam, metric, cmAssetIds.length]
  );

  const { currentData, isFetching, isError } = useGetAssetMetricsQuery(queryArg, {
    selectFromResult: ({ currentData, isFetching, isError }) => ({ currentData, isFetching, isError }),
  });

  const dataByAsset = useMemo(
    () => (currentData?.data ? groupByAsset(currentData.data, metric) : EMPTY_MAP),
    [currentData, metric]
  );

  return { dataByAsset, isFetching, isError, isTruncated: currentData?.truncated === true };
}
